/**
 * Sales Page content — all copy in one file so a non-engineer can rewrite
 * any block without touching JSX. Tone: Aussie Year 7-8 comprehension,
 * warm + punchy + a bit funny, never preachy. ~4,500 words total.
 *
 * Configurable launch URLs (override in /public/index.html via window.__HP__):
 *   CALENDAR_URL   — Calendly / Cal.com / HustleCRM booking link
 *   LEAD_FORM_URL  — POST endpoint for "Get started" form
 */

export const PAIN_POINTS = [
  {
    icon: "💸",
    title: "Watching 30% disappear",
    body: "Every Uber Eats and DoorDash order chews a chunk off the top before the chips even hit the fryer. By Friday night you're cooking for them, not you.",
  },
  {
    icon: "📞",
    title: "Phone won't stop ringing",
    body: "Bookings, cancellations, allergy questions, 'can we have a table near the window?' — and you're trying to plate up for table 12. Something's gotta give.",
  },
  {
    icon: "📄",
    title: "Menu changes are a nightmare",
    body: "Print menus get out of date the day they come off the press. Your website? Updated last Easter. Specials board? Smudged. The whole thing is held together with tape and good vibes.",
  },
  {
    icon: "⭐",
    title: "Reviews are running the show",
    body: "Three Google reviews from people who never even ate here, and now Mrs Henderson from up the road is asking questions. There's no easy way to reply, fix it, or get the happy ones to post.",
  },
  {
    icon: "🤖",
    title: "Tech companies that don't get hospo",
    body: "Big platforms charge like wounded bulls and treat you like a number. When something breaks at 7pm on a Saturday, good luck getting a human on the phone.",
  },
  {
    icon: "🧮",
    title: "You're juggling five logins",
    body: "POS, booking platform, delivery apps, email marketing, reviews — five tabs, five passwords, five bills. One thing breaks and the whole evening goes pear-shaped.",
  },
];

export const SOLUTION_TILES = [
  { n: "01", h: "One platform, all of it" },
  { n: "02", h: "Take bookings & orders direct" },
  { n: "03", h: "Update prices in 5 seconds" },
  { n: "04", h: "Stop paying delivery commissions" },
  { n: "05", h: "Looks the goods on mobile" },
  { n: "06", h: "Built by folks who get it" },
];

export const FEATURES = [
  { icon: "UtensilsCrossed", h: "A menu that's actually editable", p: "Change a price, add a special, mark something sold-out — from your phone, in seconds. The website updates instantly. No web designer required." },
  { icon: "CalendarClock", h: "Bookings that don't double-up", p: "Real-time table availability, no overbooking, no surprises. Customers reserve in three taps. You see everything on one screen." },
  { icon: "ShoppingBag", h: "Pre-orders + table-side QR", p: "Customers can order food before they arrive, or scan a code at the table. Goes straight to the kitchen. No more flagging down staff." },
  { icon: "Bike", h: "Delivery without the 30% cut", p: "Plug into Uber Direct, DoorDash Drive, or local Aussie couriers — pay a flat $8-15 per delivery instead of giving away a third of your takings." },
  { icon: "Printer", h: "Print-ready PDF menus", p: "A4 menus, wine lists, daily specials, takeaway flyers, even table tent cards with QR codes — all auto-styled to match your brand. Download, print, done." },
  { icon: "Bell", h: "SMS, email & push that won't get you fined", p: "Spam-Act compliant out of the box. Customers opt in by category — bookings, specials, loyalty. They can also opt out with one tap." },
  { icon: "CreditCard", h: "Every way customers want to pay", p: "Stripe + Square + Apple Pay + Google Pay + Afterpay + Zip. Tips, gift cards, split bills seat-by-seat. Money lands in your account, not someone else's." },
  { icon: "TicketPercent", h: "Vouchers, loyalty & gift cards", p: "Sell gift cards online, run birthday perks, reward regulars — all redeemable at checkout or by QR. No third-party loyalty card required." },
  { icon: "Globe", h: "Speaks 30+ languages", p: "AI-translated menus, bookings and ordering for visiting tourists or migrant communities. Customers pick their language with a flag dropdown." },
  { icon: "BarChart3", h: "Insights you'll actually use", p: "Top dishes, busiest hours, repeat customers, average spend. No spreadsheets, no maths degree, no charts you don't understand." },
  { icon: "QrCode", h: "Table tent cards with QR codes", p: "Each table gets its own QR. Customers scan, order, pay — drink orders fly through, no staff bottleneck. Eight cards print per A4 sheet." },
  { icon: "Plug", h: "Plays nicely with Square POS", p: "Already on Square? Plug it in. Menu, prices, inventory and orders sync both ways. Edit on the till — appears on the website. Magic." },
  { icon: "ShieldAlert", h: "Allergen information, baked in", p: "Tag every menu item with allergens (gluten, nuts, dairy, shellfish, sesame and more). Customers filter the menu by what they CAN'T eat — no awkward conversations with staff, no allergy mistakes." },
  { icon: "Calculator", h: "Cost per plate, live", p: "Build the recipe behind every menu item — ingredients, quantities, supplier prices. See your COGS and profit margin update in real time as costs move. Know exactly which dishes pay the rent." },
  { icon: "Receipt", h: "Push sales to your accounting software", p: "Daily takings, GST and Stripe fees auto-export to Xero, MYOB or QuickBooks. No more Sunday-night spreadsheets. Your bookkeeper will thank you." },
  { icon: "MousePointerClick", h: "Menus that aren't dead PDFs", p: "Web PDF menus are antiquated and hard to read. Ours are clickable — tap any dish for the photo, ingredients, allergens, wine pairing and chef's note. Print to PDF only when you need the laminated A4." },
  { icon: "Zap", h: "Quick orders with smart upsells", p: "Built for the lunchtime queue — coffee, kebabs, pizza. One-tap reorder, recommended add-ons, dietary chips. Pre-order ahead or walk-in fast. Average ticket goes up, queue goes down." },
  { icon: "Cake", h: "Easy rewards & birthday club", p: "Points per dollar, birthday freebies, cross-store loyalty pool — set it up in five minutes, runs itself forever. Regulars come back 30 % more often. No plastic cards." },
];

export const COMPARISON_ROWS = [
  ["Take bookings",                "✓",       "✓",       "✓",       "—",       "✓",       "us-✓"],
  ["Take online orders",           "—",       "✓",       "—",       "✓",       "—",       "us-✓"],
  ["Table-side QR ordering",       "—",       "✓",       "—",       "✓",       "—",       "us-✓"],
  ["Direct delivery (no 30% cut)", "—",       "—",       "—",       "—",       "—",       "us-✓"],
  ["Built-in menu editor",         "✓",       "✓",       "—",       "✓",       "—",       "us-✓"],
  ["Print-ready PDF menus",        "—",       "—",       "—",       "—",       "—",       "us-✓"],
  ["Vouchers + gift cards",        "—",       "—",       "—",       "✓",       "—",       "us-✓"],
  ["AI menu import (snap a photo)", "—",      "—",       "—",       "—",       "—",       "us-✓"],
  ["Multi-language (30+)",         "—",       "—",       "—",       "—",       "—",       "us-✓"],
  ["Square POS sync",              "—",       "—",       "—",       "✓",       "—",       "us-✓"],
  ["Apple Pay + Google Pay",       "✓",       "✓",       "✓",       "✓",       "✓",       "us-✓"],
  ["Afterpay + Zip",               "—",       "—",       "—",       "—",       "—",       "us-✓"],
  ["No per-cover booking fee",     "—",       "—",       "—",       "✓",       "—",       "us-✓"],
  ["Owns your customer data",      "—",       "—",       "—",       "✓",       "—",       "us-✓"],
  ["Aussie support, no robots",    "—",       "—",       "—",       "—",       "✓",       "us-✓"],
  ["Monthly cost",                 "$249+",   "$179+",   "$300+",   "$0-99",   "$200+",   "us-$0-99"],
];
export const COMPARISON_HEADERS = ["", "OpenTable", "Mr Yum", "SevenRooms", "Square Online", "Resy", "Hustle Plate"];

export const CUISINES = [
  { c: "Italian",     t: "Trattoria · Pizza, pasta, vinos", k: "italian",     demo: "/" },
  { c: "Chinese",     t: "Jade Dragon · Yum cha & roast duck", k: "chinese",     demo: "/" },
  { c: "French",      t: "Le Petit Bistro · Provençal classics", k: "french",    demo: "/" },
  { c: "Vietnamese",  t: "Pho Mami · Banh mi & noodles",        k: "vietnamese", demo: "/" },
  { c: "Japanese",    t: "Ichiban · Izakaya & sushi",           k: "japanese",   demo: "/" },
  { c: "Thai",        t: "Som Tam · Bangkok street food",       k: "thai",       demo: "/" },
  { c: "Indian",      t: "Tandoor & Tales · Punjabi feasts",    k: "indian",     demo: "/" },
  { c: "Lebanese",    t: "Beirut Garden · Mezze & shawarma",    k: "lebanese",   demo: "/" },
];

export const TESTIMONIALS = [
  {
    quote: "We were paying Uber Eats more than we paid our chef. HustlePlate switched that around in a fortnight. The direct-order setup paid for itself on the first Friday night.",
    name: "Tony Marchetti", role: "Owner · Trattoria, Carlton VIC",
    logo: "BC",
  },
  {
    quote: "Forty bookings on opening night and not one stuff-up. The phone barely rang. Reckon I might actually take a Sunday off this winter.",
    name: "Mei Lin", role: "Operator · Jade Dragon, Sydney NSW",
    logo: "JD",
  },
  {
    quote: "Built the wine list, took the bookings, sorted the deliveries — all from my phone, in bed, with a glass of pinot. Couldn't make this up.",
    name: "Pierre Doucet", role: "Sommelier · Le Petit Bistro, Fitzroy VIC",
    logo: "LP",
  },
  {
    quote: "Square gave us the till. These mob gave us the rest. The two talk to each other like they grew up together.",
    name: "Sam Tran", role: "Owner · Pho Mami, Cabramatta NSW",
    logo: "PM",
  },
  {
    quote: "Pricing was the kicker. Free to start, no per-cover fees, no commission on deliveries. My accountant has stopped wincing.",
    name: "Anita Kapoor", role: "Manager · Tandoor & Tales, Harris Park NSW",
    logo: "TT",
  },
  {
    quote: "We switched from OpenTable on a Tuesday, ran the lunch service on HustlePlate the same arvo. Staff were across it in twenty minutes. Customers noticed nothing — except the kitchen got quieter.",
    name: "Hugo Bennett", role: "Owner · The Loft Kebab Zone, Oxley QLD",
    logo: "KZ",
  },
];

export const FAQS = [
  {
    q: "Is it really free to start?",
    a: "Yep. The Starter plan is free, forever. You get the menu, bookings, a storefront, payments, and one of our 70+ templates. We make money when you grow into Standard or Pro — not before. No credit card needed to sign up.",
  },
  {
    q: "What if I'm already on Square / OpenTable / Mr Yum?",
    a: "Keep them. We plug into Square POS so menus sync both ways. We replace OpenTable and Mr Yum entirely (and at a fraction of the cost), but you can keep them running side-by-side while you switch over — your customers won't see a single hiccup.",
  },
  {
    q: "Do you charge a commission on bookings or orders?",
    a: "Never. No per-cover fee. No commission on orders. No skimming. The Stripe/Square payment processor takes their usual 1.5-2.6% — that goes to them, not us. Anything else stays in your pocket.",
  },
  {
    q: "How do you do delivery without the 30% cut?",
    a: "Same drivers, different product. Uber Eats and DoorDash also sell a service called 'Direct' or 'Drive' — you take the order on your platform, they just send a driver. Costs a flat $8-15 per delivery instead of 30% of the bill. We plug into Uber Direct, DoorDash Drive and Aussie courier Sherpa. Customers get tracking branded to YOUR restaurant.",
  },
  {
    q: "What if my menu is on a PDF / Word doc / chalkboard?",
    a: "Snap a photo of it with your phone. Our AI reads the photo, pulls out every dish, price, allergen and category — usually in under 30 seconds. You review, hit Approve, and you're live. Works on takeaway flyers, dinner menus, even chalkboards. (Yes, really.)",
  },
  {
    q: "Will it work on my customers' phones?",
    a: "Mobile-first design. The site is fast, taps work, photos pop, the whole thing feels like a proper app — not a shrunk-down desktop site. We can also wrap it as a real iOS/Android app on the App Store so customers can install it.",
  },
  {
    q: "How long does setup take?",
    a: "Most cafés are live in under an hour. Restaurants with bigger menus, a couple of hours. We handle the heavy lifting — you pick a template, snap a photo of your menu, add your hours, paste in your Stripe or Square key, and you're trading.",
  },
  {
    q: "What if something breaks at 7pm on a Saturday?",
    a: "We're Aussie-owned and we run real humans on support, not chatbots. SMS, email or call — proper response times during trading hours, plus an on-call engineer for outages. No queues, no overseas accent you can't understand, no 'please open a ticket'.",
  },
  {
    q: "Can I migrate my existing customer / booking data?",
    a: "Yep. CSV, Excel, exports from OpenTable or Mr Yum — drop them in, we map the fields with AI, you click commit. Done in minutes, not days. We've also done custom migrations from POS-only systems for big chains.",
  },
  {
    q: "What about gift cards, vouchers and loyalty?",
    a: "Pro plan and up. Sell gift cards on the website (Stripe processes the payment, code arrives via email), set up birthday vouchers, anniversary perks, points-based loyalty — all redeemable at checkout or via QR. No third-party loyalty card required.",
  },
  {
    q: "Is my data safe?",
    a: "Bank-grade encryption in transit, encrypted at rest, hosted in Australian data centres, GDPR + Australian Privacy Act compliant. We don't share, sell or look at your customer data. Ever. (We've signed paperwork promising as much.)",
  },
  {
    q: "Can I leave whenever I want?",
    a: "No contracts. No lock-in. Cancel any time from your admin. We'll export every byte of your data — customers, bookings, orders, menu — as CSV so you can take it with you. No hard feelings; we'll buy you a beer if you ever swing past.",
  },
];

export const STEPS = [
  { n: "1", h: "Pick a template",      p: "70+ designs, every cuisine, every vibe. Pick one, swap colours, you're 80% done." },
  { n: "2", h: "Import your menu",     p: "Snap a photo, drop a PDF, paste a URL, or upload a CSV. AI does the rest." },
  { n: "3", h: "Add your hours & Stripe", p: "Five-minute setup. Plug in your bank account, pick your opening hours." },
  { n: "4", h: "Hit publish",          p: "Custom domain optional. Your site is live, taking bookings and orders." },
];

export const HERO_CARDS = [
  { eyebrow: "Bookings", h: "Table for 4, 7:30pm", b: "Sam Tran · 0411 222 333" },
  { eyebrow: "Order in", h: "$87.40 · Pad Thai + 2x sides", b: "Picked up via DoorDash Drive" },
  { eyebrow: "Live now", h: "32 visitors browsing menu", b: "12 added items to cart" },
];
